/**
 * One student's insight: how they worked, the story of the session scene by
 * scene, where they got stuck, anything worth a closer look, and what the
 * recording measured. Everything cited opens the evidence behind it.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { labInsights } from '../services/api';
import { Chip, ConceptChip, EvidenceImage, Stat, SCENE_ACTIVITIES, WORK_PATTERNS, errorMessage, timeOf, whenOf } from '../components/LabInsightsParts';

const section = { padding: '16px 20px', display: 'grid', gap: 12 };
const heading = { fontSize: 16, margin: 0 };

/** The evidence an item cites: screenshots and scenes open; events and code say what they are. */
function Cited({ ids, evidence, ctx }) {
  if (!ids?.length) return null;
  const known = new Map(evidence.map((item) => [item.id, item]));
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {ids.map((id) => {
        const item = known.get(id);
        if (!item) return null;
        if (item.kind === 'screenshot' || item.kind === 'scene') {
          return <EvidenceImage key={id} {...ctx} evidenceId={id} when={item.at} label={`${id} · ${timeOf(item.at)}`} />;
        }
        const text = item.kind === 'code' ? `${id}: ${item.path}` : item.kind === 'facts' ? 'measured facts' : `${id}: ${item.why}${item.at ? ` (${timeOf(item.at)})` : ''}`;
        return <Chip key={id} title={text}>{text.length > 70 ? `${text.slice(0, 69)}…` : text}</Chip>;
      })}
    </div>
  );
}

function Story({ story, ctx, unfinished }) {
  const [all, setAll] = useState(false);
  if (!story?.scenes?.length) {
    return <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
      {unfinished ? "The story appears when this student's analysis has finished." : 'No screenshots were recorded for this student.'}
    </p>;
  }
  const telling = (scene) => scene.caption || scene.activity.runs || scene.activity.maxEditorErrors || scene.activity.pasted >= 200
    || scene.activity.awaySeconds >= 20 || scene.activity.edits;
  const shown = all ? story.scenes : story.scenes.filter(telling);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
        {story.frames} screenshots, {story.scenes.length} scenes (a new one wherever the screen changed
        {story.mode === 'size' ? ', judged by image size for this older recording' : ''})
        {story.described ? `; ${story.described} described by the AI` : ''}.
      </p>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {shown.map((scene) => (
          <li key={scene.id} style={{ display: 'grid', gridTemplateColumns: '92px 1fr auto', gap: 12, alignItems: 'start',
            padding: '8px 0', borderTop: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
              {timeOf(scene.start)}<br />{scene.frames > 1 ? `to ${timeOf(scene.end)}` : ''}
            </span>
            <div style={{ display: 'grid', gap: 4 }}>
              {scene.caption && <span style={{ lineHeight: 1.5 }}>{scene.caption}</span>}
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {scene.activityKind && <><Chip>{SCENE_ACTIVITIES[scene.activityKind] || scene.activityKind}</Chip>{' '}</>}
                {scene.activity.summary}
              </span>
            </div>
            <EvidenceImage {...ctx} evidenceId={scene.id} when={scene.start} label={scene.id} />
          </li>
        ))}
      </ol>
      {story.scenes.length > shown.length || all ? (
        <button type="button" className="btn btn-secondary btn-sm" style={{ justifySelf: 'start' }} onClick={() => setAll(!all)}>
          {all ? 'Show only the scenes with something in them' : `Show all ${story.scenes.length} scenes`}
        </button>
      ) : null}
    </div>
  );
}

const UNFINISHED = ['pending', 'queued', 'running'];

export default function StudentInsight() {
  const { labId, runId, studentInsightId } = useParams();
  const query = useQuery({
    queryKey: ['studentInsight', runId, studentInsightId],
    queryFn: () => labInsights.student(labId, runId, studentInsightId),
    refetchInterval: (current) => (UNFINISHED.includes(current?.state?.data?.status) ? 5000 : false),
  });
  if (query.isPending) return <p role="status">Loading the student's report…</p>;
  if (query.isError) return <p role="alert">{errorMessage(query.error, 'Could not read the student\'s report.')}</p>;
  const data = query.data;
  const report = data.report;
  const signals = data.signals;
  const ctx = { labId, runId, studentInsightId };
  const evidence = data.evidence || [];
  const unfinished = UNFINISHED.includes(data.status);

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 1100 }}>
      <div><Link className="btn btn-secondary btn-sm" to={`/labs/${labId}/insights`}><ArrowLeft size={16} aria-hidden="true" /> Back to the lab's insights</Link></div>
      <div style={{ display: 'grid', gap: 4 }}>
        <h1 style={{ margin: 0 }}>{data.name || data.studentId}</h1>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          {data.studentId} · {data.lab.name}{data.completedAt ? ` · analysed ${whenOf(data.completedAt)}` : ''}{data.model && report ? ` with ${data.model}` : ''}
        </p>
      </div>
      {unfinished && <p role="status" style={{ margin: 0 }}>This student's analysis has not finished yet; this page updates by itself.</p>}
      {data.error && <p role="status" style={{ margin: 0, color: data.status === 'failed' ? 'var(--accent-danger)' : 'var(--accent-warning)' }}>{data.error}</p>}

      {report && (
        <section className="card" style={section} aria-labelledby="summary">
          <h2 id="summary" style={heading}>How they worked</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{report.workPattern && <Chip color="var(--accent-primary)">{WORK_PATTERNS[report.workPattern]}</Chip>}</div>
          <p style={{ margin: 0, lineHeight: 1.6, maxWidth: '75ch' }}>{report.summary}</p>
          {report.progress && <p style={{ margin: 0, maxWidth: '75ch' }}><strong>How far they got:</strong> {report.progress}</p>}
          {[['Strengths', report.strengths], ['Next steps', report.nextSteps]].filter(([, items]) => items?.length).map(([title, items]) => (
            <div key={title}>
              <strong>{title}</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20, display: 'grid', gap: 4 }}>{items.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          ))}
        </section>
      )}

      <section className="card" style={section} aria-labelledby="story">
        <h2 id="story" style={heading}>What happened</h2>
        <Story story={data.story} ctx={ctx} unfinished={unfinished} />
      </section>

      {report && (
        <section className="card" style={section} aria-labelledby="stuck">
          <h2 id="stuck" style={heading}>Where they got stuck</h2>
          {!report.blockages.length && <p style={{ margin: 0 }}>No lasting difficulty shows in the recording.</p>}
          {report.blockages.map((b) => (
            <article key={b.title} style={{ display: 'grid', gap: 6, paddingBottom: 12, borderBottom: '1px solid var(--border-primary)' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{b.title}</strong> <ConceptChip concept={b.concept} />
                {b.minutes != null && <Chip>{b.minutes} min</Chip>}
                {b.resolved != null && <Chip color={b.resolved ? 'var(--accent-success)' : 'var(--accent-warning)'}>{b.resolved ? 'Got past it' : 'Not resolved'}</Chip>}
              </div>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{b.description}</p>
              {b.suggestion && <p style={{ margin: 0, fontSize: 14 }}><strong>Suggestion:</strong> {b.suggestion}</p>}
              <Cited ids={b.evidence} evidence={evidence} ctx={ctx} />
            </article>
          ))}
        </section>
      )}

      {report && (
        <section className="card" style={section} aria-labelledby="closer">
          <h2 id="closer" style={heading}>Worth a closer look</h2>
          {report.integrity.level !== 'review' && <p style={{ margin: 0 }}>Nothing in the recording needs a closer look.</p>}
          {report.integrity.observations.map((o) => (
            <article key={o.what} style={{ display: 'grid', gap: 6 }}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{o.what}</p>
              <p style={{ margin: 0, fontSize: 14 }}><strong>Innocent explanations:</strong> {o.innocentExplanations.join('; ')}</p>
              <Cited ids={o.evidence} evidence={evidence} ctx={ctx} />
            </article>
          ))}
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <AlertTriangle size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
            Observations to check against the evidence, never a verdict: the recording cannot see another device, a second
            window outside the IDE, or permitted help.
          </p>
        </section>
      )}

      {signals && (
        <section className="card" style={section} aria-labelledby="measured">
          <h2 id="measured" style={heading}>Measured from the recording</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Stat label="active minutes" value={signals.time.activeMinutes} hint={`${signals.time.sessionCount} session(s)`} />
            <Stat label="away from the IDE" value={`${signals.time.awayFromIde.count}×`} hint={`${signals.time.awayFromIde.minutes} min in all`} />
            <Stat label="runs" value={signals.runs.total} hint={`${signals.runs.failed} failed${signals.runs.stopped ? `, ${signals.runs.stopped} stopped by the student` : ''}`} />
            <Stat label="re-ran unchanged code after a failure" value={signals.runs.unchangedRerunsAfterFailure} />
            <Stat label="minutes with errors in the editor" value={signals.editorErrors?.recorded ? signals.editorErrors.totalMinutesWithErrors : '—'}
              hint={signals.editorErrors?.recorded ? null : 'not recorded by this image'} />
            <Stat label="code pasted" value={`${Math.round((signals.insertions.pastedShare || 0) * 100)}%`} hint={`${signals.insertions.largeFromOutside} large, not copied in the IDE`} />
          </div>
          {!!signals.runs.errorThemes?.length && (
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
              {signals.runs.errorThemes.map((t) => <li key={t.theme}>{t.label}: {t.count} failed run{t.count === 1 ? '' : 's'}</li>)}
            </ul>
          )}
          {signals.coverage.recorder === 'earlier' && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              This recording comes from an environment started before Virtual Labs recorded Run-button runs, errors shown in the
              editor and screen changes (October 2026). Runs are counted from terminal commands, where pressing Run again on the
              same file shows only once, and the scenes are cut by image size.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
